/**
 * IMPORTANT: English comments only. DO NOT use Chinese characters in comments.
 * All Chinese comments will be removed during code reviews.
 */
import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import AddressModal from './map/AddressModal';
import MapLocationModal from './map/MapLocationModal';
import GoogleMapsLoader from './map/GoogleMapsLoader';
import { useAddressContext } from '../context/AddressContext';
import { useLocationManager } from '../hooks/useLocationManager';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { toast } from 'react-hot-toast';
import { Address as BaseAddress } from '../types/api';
import addressService, { AddressWithId } from '../services/addressService';
import { useMapStore } from '../lib/map/store';
import { useTranslation } from 'react-i18next';
import { 
  standardizeLocation, 
  prepareForSaving,
  saveTemporaryLocation,
  getTemporaryLocation,
  clearTemporaryLocation
} from '../lib/map/locationUtils';

// Delete confirmation state interface
interface DeleteConfirmState {
  show: boolean;
  addressId: string | null;
  addressIndex: number | null;
}

interface AddressManagementModalProps {
  onAddressSelect: (address: AddressWithId) => void;
  isOpen: boolean;
  onClose: () => void;
  selectedAddress?: AddressWithId | null;
  isSubscription?: boolean;
}

export default function AddressManagementModal({
  onAddressSelect,
  isOpen,
  onClose,
  selectedAddress,
  isSubscription = false,
}: AddressManagementModalProps) {
  const { t } = useTranslation();

  console.log('[AddressManagementModal] Props received:', {
    isOpen,
    selectedAddress,
    onAddressSelect: typeof onAddressSelect === 'function' ? 'function' : typeof onAddressSelect
  });

  console.log('[AddressManagementModal] Rendering with isOpen:', isOpen);
  console.log('[AddressManagementModal] Selected address:', selectedAddress);
  console.log('[AddressManagementModal] Selected address type:', selectedAddress ? typeof selectedAddress : 'null');
  console.log('[AddressManagementModal] Selected address properties:', selectedAddress ? Object.keys(selectedAddress).join(', ') : 'none');
  
  // Get current user data to access phone number
  const { user } = useCurrentUser();
  
  // Use the AddressContext instead of the AddressManager hook
  const {
    addresses,
    selectedAddress: contextSelectedAddress,
    isLoading,
    error,
    selectAddress,
    addAddress: handleAddAddress,
    deleteAddress: handleDeleteAddressById,
    refreshAddresses
  } = useAddressContext();
  
  // Add modal stability tracking
  const [isStable, setIsStable] = useState(false);
  
  // Add a ref to track selection in progress
  const selectionInProgressRef = useRef(false);
  
  // Ensure onAddressSelect is a function and is memoized
  const safeOnAddressSelect = useCallback((address: AddressWithId) => {
    console.log('[AddressManagementModal] safeOnAddressSelect called with address:', address);
    
    // Use ref instead of static property for more reliable tracking
    if (selectionInProgressRef.current) {
      console.warn('[AddressManagementModal] Selection already in progress, preventing duplicate');
      return;
    }
    
    // Skip if the address is the same as already selected
    if (selectedAddress && address && selectedAddress.id === address.id) {
      console.log('[AddressManagementModal] Skipping selection of already selected address:', address.id);
      return;
    }
    
    // Mark selection in progress
    selectionInProgressRef.current = true;
    
    if (typeof onAddressSelect === 'function') {
      try {
        // Create a clean copy of the address to avoid reference issues
        const addressCopy = {...address};
        console.log('[AddressManagementModal] Calling onAddressSelect with address copy:', addressCopy);
        onAddressSelect(addressCopy);
        console.log('[AddressManagementModal] onAddressSelect call completed');
        
        // Also update in context
        selectAddress(addressCopy);
        
        // Force modal to close immediately after selection
        if (typeof onClose === 'function') {
          console.log('[AddressManagementModal] Closing modal immediately after selection');
          onClose();
        }
      } catch (err) {
        console.error('[AddressManagementModal] Error in onAddressSelect:', err);
      }
    } else {
      console.warn('[AddressManagementModal] onAddressSelect is not a function');
    }
    
    // Clear selection flag after delay
    setTimeout(() => {
      selectionInProgressRef.current = false;
    }, 100);
  }, [onAddressSelect, selectAddress, onClose, selectedAddress]);
  
  // Control which modal is visible - ALWAYS initialize to address
  const [activeModal, setActiveModal] = useState<'address' | 'map'>('address');
  
  // Map initial coordinates
  const [initialMapCoords, setInitialMapCoords] = useState<{ lat: number; lng: number } | undefined>(undefined);
  
  // Keep track of whether modal has been opened before
  const [hasBeenOpened, setHasBeenOpened] = useState(false);
  
  // Track if initial data load is complete
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  
  // Track if we've already fetched empty addresses to avoid infinite loops
  const [hasEmptyAddressesFetched, setHasEmptyAddressesFetched] = useState(false);
  
  // Add address editing state and related functions
  const [editingAddress, setEditingAddress] = useState<AddressWithId | null>(null);
  
  // Add delete confirmation state
  const [deleteConfirm, setDeleteConfirm] = useState<DeleteConfirmState>({
    show: false, 
    addressId: null,
    addressIndex: null
  });
  
  // When the modal is opened, mark it as stable after a short delay
  useEffect(() => {
    if (isOpen) {
      // Reset stability when opening
      setIsStable(false);
      
      // Set stable after a short delay
      const stabilityTimer = setTimeout(() => {
        console.log('[AddressManagementModal] Modal is now stable');
        setIsStable(true);
      }, 300);
      
      return () => clearTimeout(stabilityTimer);
    }
  }, [isOpen]);
  
  // When the modal opens for the first time, mark it as opened
  useEffect(() => {
    if (isOpen && !hasBeenOpened) {
      setHasBeenOpened(true);
    }
  }, [isOpen, hasBeenOpened]);
  
  // When addresses are loaded, mark initial load as complete
  useEffect(() => {
    if (!initialLoadComplete && !isLoading) {
      console.log(`[AddressManagementModal] Initial data loaded: ${addresses.length} addresses`);
      setInitialLoadComplete(true);
      
      // If we loaded 0 addresses, mark that we've fetched empty addresses
      if (addresses.length === 0) {
        console.log('[AddressManagementModal] Empty addresses loaded, marking as complete');
        setHasEmptyAddressesFetched(true);
      }
      
      // Only attempt to fix/match addresses when explicitly opening the modal for address selection
      // NOT during initial page load or component mounting
      if (isOpen && selectedAddress && !selectedAddress.id && addresses.length > 0) {
        console.log('[AddressManagementModal] Selected address has no ID, attempting to find match');
        
        // Try to find matching address by street and city or other available fields
        const matchingAddress = addresses.find(addr => 
          (selectedAddress.street && addr.street === selectedAddress.street) &&
          (selectedAddress.city && addr.city === selectedAddress.city)
        );
        
        if (matchingAddress) {
          console.log('[AddressManagementModal] Found matching address with ID:', matchingAddress.id);
          // Only update if we're showing the address modal - this prevents unwanted API calls on mount
          if (activeModal === 'address' && isStable) {
            console.log('[AddressManagementModal] Modal is active and stable, selecting matching address');
            safeOnAddressSelect(matchingAddress);
          } else {
            console.log('[AddressManagementModal] Modal not active or not stable, skipping auto-selection');
          }
        }
      }
    }
  }, [addresses.length, initialLoadComplete, isLoading, isOpen, activeModal, isStable, safeOnAddressSelect, selectAddress]);

  // When the modal opens, refresh addresses
  useEffect(() => {
    if (isOpen) {
      console.log(`[AddressManagementModal] Modal opened with isOpen=${isOpen}`);
      console.log(`[AddressManagementModal] Has ${addresses.length} addresses, isLoading=${isLoading}`);
      
      // Force a refresh when the modal opens with a slight delay to ensure UI is visible first
      const timeoutId = setTimeout(() => {
        // Only perform operations if the modal is still open
        if (!isOpen) {
          console.log('[AddressManagementModal] Modal closed before fetch could start');
          return;
        }
        
        // If modal opens but no addresses are loaded yet, fetch them
        if (addresses.length === 0 && !isLoading && !hasEmptyAddressesFetched) {
          console.log('[AddressManagementModal] No addresses found, triggering fetch');
          refreshAddresses().then(fetchedAddresses => {
            // Check if modal is still open before processing results
            if (!isOpen) {
              console.log('[AddressManagementModal] Modal closed during fetch, abandoning result processing');
              return;
            }
            
            // After fetch, if we still have 0 addresses, mark it so we don't fetch again
            if (fetchedAddresses.length === 0) {
              console.log('[AddressManagementModal] Fetched 0 addresses, marking empty state');
              setHasEmptyAddressesFetched(true);
            } else {
              console.log(`[AddressManagementModal] Fetched ${fetchedAddresses.length} addresses`);
              
              // Only auto-select if there's no selection yet
              if (fetchedAddresses.length > 0 && !selectedAddress?.id && !selectionInProgressRef.current) {
                console.log('[AddressManagementModal] Auto-selecting first address after fetch');
                // Immediately select without delay to ensure modal closes
                if (isOpen && !selectedAddress?.id && !selectionInProgressRef.current) {
                  safeOnAddressSelect(fetchedAddresses[0]);
                }
              }
            }
          }).catch(err => {
            console.error('[AddressManagementModal] Error fetching addresses:', err);
            // Mark as fetched even on error to prevent infinite retries
            setHasEmptyAddressesFetched(true);
          });
        }
      }, 300);
      
      return () => clearTimeout(timeoutId);
    } else {
      // When modal closes, reset the empty addresses flag if user adds an address elsewhere
      // This ensures next time they open the modal, we'll check again
      console.log('[AddressManagementModal] Modal closed, resetting empty addresses flag');
      setHasEmptyAddressesFetched(false);
    }
  }, [isOpen, hasEmptyAddressesFetched, addresses.length, isLoading, refreshAddresses, selectedAddress, safeOnAddressSelect]);
  
  // Wrap onClose with logging to identify why it might be closing
  const safeOnClose = () => {
    console.log('[AddressManagementModal] Close requested explicitly by user action');
    onClose();
  };

  // Use location manager for current location
  const { getCurrentLocation } = useLocationManager();
  
  // Handle selecting a location from map
  const handleSelectLocation = async (location: any) => {
    console.log('[AddressManagementModal] Location selected:', location);
    
    try {
      // Check if we already have a saved address object (from MapLocationModal)
      if (location.id) {
        console.log('[AddressManagementModal] Using pre-saved address with ID:', location.id);
        
        // If we already have a complete address object (with ID), use it directly
        safeOnAddressSelect(location);
        
        // Force refresh the address list
        await refreshAddresses();
        console.log('[AddressManagementModal] Addresses refreshed after selection');
        
        // Return to address list and refresh
        setActiveModal('address');
        return;
      }
      
      // Standardize the input location data
      const standardizedLocation = standardizeLocation(location);
      
      // Clear any temporary storage
      clearTemporaryLocation();
      
      // Convert to API format
      const newAddress: BaseAddress = prepareForSaving(standardizedLocation, {
        id: '', // Adding empty id to satisfy the type requirement
        contactName: location.contactName,
        contactPhone: location.contactPhone
      }) as BaseAddress;
      
      console.log('[AddressManagementModal] Creating new address with standardized data:', newAddress);
      
      // Add address and get result
      const createdAddress = await handleAddAddress(newAddress);
      
      if (createdAddress) {
        console.log('[AddressManagementModal] New address created successfully:', createdAddress);
        
        // Force refresh to get the latest addresses including the new one
        try {
          // Explicitly force cache invalidation to ensure we get fresh data
          await refreshAddresses();
          
          // Always select the new address
          safeOnAddressSelect(createdAddress);
        } catch (refreshError) {
          console.error('[AddressManagementModal] Error refreshing addresses after creation:', refreshError);
        }
        
        // Return to address list
        setActiveModal('address');
      } else {
        console.error('[AddressManagementModal] Failed to create new address');
        toast.error('Failed to create new address. Please try again.');
      }
    } catch (error) {
      console.error('[AddressManagementModal] Error handling location selection:', error);
      toast.error('Something went wrong. Please try again.');
    }
  };
  
  // Handle opening the map modal
  const handleOpenMapModal = async () => {
    console.log('[AddressManagementModal] Opening map modal');
    
    // Make sure to clear any previously selected address
    if (selectionInProgressRef.current) {
      selectionInProgressRef.current = false;
    }
    
    // Get current map data from the store
    const { markerPosition, address, locationName, addressDetails } = useMapStore.getState();
    
    // Log complete store state for debugging
    console.log('[AddressManagementModal] Current store state when opening map:', {
      markerPosition,
      address,
      locationName,
      addressDetails
    });
    
    // Determine initial coordinates for the map
    let mapInitialCoords = { lat: 18.800341, lng: 98.968211 }; // Default to Chiangmai
    
    // First priority: use address details from search/autocomplete if available
    if (addressDetails && addressDetails.latitude && addressDetails.longitude) {
      console.log('[AddressManagementModal] Using addressDetails coordinates:', {
        lat: addressDetails.latitude,
        lng: addressDetails.longitude
      });
      mapInitialCoords = {
        lat: addressDetails.latitude,
        lng: addressDetails.longitude
      };
      
      // Save to temporary storage for persistence
      try {
        saveTemporaryLocation({
          position: mapInitialCoords,
          address: addressDetails.formatted_address || address,
          locationName: addressDetails.name || locationName
        });
      } catch (error) {
        console.error('[AddressManagementModal] Error saving location to temp storage:', error);
      }
    }
    // Second priority: use marker position from store if not the default
    else if (markerPosition && markerPosition.lat !== 0) {
      console.log('[AddressManagementModal] Using position from store:', markerPosition);
      mapInitialCoords = markerPosition;
      
      // Save to temporary storage
      if (address) {
        try {
          saveTemporaryLocation({
            position: markerPosition,
            address,
            locationName
          });
        } catch (error) {
          console.error('[AddressManagementModal] Error saving location to temp storage:', error);
        }
      }
    }
    // Third priority: try to get current location if using default
    else if (mapInitialCoords.lat === 18.800341 && navigator.geolocation) {
      try {
        console.log('[AddressManagementModal] Attempting to get current location');
        // Create a promise to get current location
        const position = await new Promise<GeolocationPosition>((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            timeout: 5000,
            maximumAge: 0
          });
        });
        
        // Extract coordinates
        const coords = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        
        console.log('[AddressManagementModal] Setting map to current location:', coords);
        mapInitialCoords = coords;
      } catch (error) {
        console.error('[AddressManagementModal] Error getting current location:', error);
        // Keep default coordinates if getting current location fails
      }
    }
    
    // Set initial coordinates for the map
    setInitialMapCoords(mapInitialCoords);
    console.log('[AddressManagementModal] Set initial map coordinates to:', mapInitialCoords);
    
    // Switch to map modal
    setActiveModal('map');
  };
  
  // Handle closing map modal and return to address list
  const handleCloseMapModal = async () => {
    console.log('[AddressManagementModal] Closing map modal');
    
    // Refresh addresses when returning to address list
    try {
      // Use await to ensure addresses are loaded before changing the view
      await refreshAddresses();
      console.log(`[AddressManagementModal] Address refresh complete, got ${addresses.length} addresses`);
      
      // If we have addresses and a selected address doesn't exist, select the first one
      if (addresses.length > 0 && (!selectedAddress || !addresses.some(addr => addr.id === selectedAddress.id))) {
        console.log('[AddressManagementModal] Auto-selecting first address after refresh');
        safeOnAddressSelect(addresses[0]);
      }
    } catch (error) {
      console.error('[AddressManagementModal] Error refreshing addresses:', error);
    }
    
    // Switch back to address modal
    setActiveModal('address');
    setEditingAddress(null); // Clear editing state
  };
  
  // Handle closing the entire flow
  const handleCloseAll = () => {
    console.log('[AddressManagementModal] Closing all modals');
    
    // Check if we have a selected address with ID
    // If not, try to select a default address if available
    if (!selectedAddress?.id && addresses.length > 0 && !selectionInProgressRef.current) {
      // Try to find default address or use first one
      const defaultAddress = addresses.find(addr => addr.isDefault) || addresses[0];
      if (defaultAddress) {
        console.log('[AddressManagementModal] Auto-selecting address before closing:', defaultAddress.id);
        // Use safe selection function which includes debouncing
        safeOnAddressSelect(defaultAddress);
      }
    }
    
    safeOnClose();
  };
  
  // Handle editing address
  const handleEditAddress = (address: AddressWithId) => {
    console.log('[AddressManagementModal] Editing address:', {
      id: address.id,
      name: address.name,
      locationName: address.locationName,
      details: address.details,
      street: address.street,
      city: address.city,
      state: address.state,
      zipCode: address.zipCode,
      country: address.country,
      contactName: address.contactName,
      contactPhone: address.contactPhone
    });
    
    // Get complete address information to ensure all necessary fields are included
    addressService.getAddressById(address.id)
      .then(fullAddressData => {
        if (fullAddressData) {
          console.log('[AddressManagementModal] Retrieved full address data:', fullAddressData);
          
          // Construct complete address object with all fields
          const completeAddress: AddressWithId = {
            ...address,
            ...fullAddressData,
            // If details field doesn't exist, try to build from other fields
            details: fullAddressData.details || address.details || 
              `${address.street || ''}${address.city ? `, ${address.city}` : ''}${address.state ? `, ${address.state}` : ''}${address.zipCode ? ` ${address.zipCode}` : ''}${address.country ? `, ${address.country}` : ''}`
          };
          
          console.log('[AddressManagementModal] Sending complete address to edit mode:', completeAddress);
          setEditingAddress(completeAddress);
          setActiveModal('map');
        } else {
          // If unable to get complete information, use existing information
          console.log('[AddressManagementModal] Could not retrieve full address data, using available info');
          
          // Ensure address object has details
          const completeAddress = {
            ...address,
            // If details field doesn't exist, try to build from other fields
            details: address.details || 
              `${address.street || ''}${address.city ? `, ${address.city}` : ''}${address.state ? `, ${address.state}` : ''}${address.zipCode ? ` ${address.zipCode}` : ''}${address.country ? `, ${address.country}` : ''}`
          };
          
          setEditingAddress(completeAddress);
          setActiveModal('map');
        }
      })
      .catch(error => {
        console.error('[AddressManagementModal] Error retrieving full address:', error);
        
        // If error occurs, try to use existing information
        const completeAddress = {
          ...address,
          details: address.details || 
            `${address.street || ''}${address.city ? `, ${address.city}` : ''}${address.state ? `, ${address.state}` : ''}${address.zipCode ? ` ${address.zipCode}` : ''}${address.country ? `, ${address.country}` : ''}`
        };
        
        setEditingAddress(completeAddress);
        setActiveModal('map');
      });
  };
  
  // Modified delete address handler, now only shows confirmation dialog
  const handleDeleteAddressRequest = (address: AddressWithId, index: number): Promise<boolean> => {
    console.log('[AddressManagementModal] Requesting to delete address:', address.id);
    
    // Show confirmation dialog
    setDeleteConfirm({
      show: true,
      addressId: address.id,
      addressIndex: index
    });
    
    // Return a resolved Promise with false, indicating deletion is not yet complete
    // The actual delete operation will be performed by confirmDeleteAddress after user confirmation
    return Promise.resolve(false);
  };
  
  // Function to confirm deletion
  const confirmDeleteAddress = async () => {
    console.log('[AddressManagementModal] Confirming delete for address:', deleteConfirm.addressId);
    
    if (!deleteConfirm.addressId || deleteConfirm.addressIndex === null) {
      console.error('[AddressManagementModal] Missing address ID or index for delete confirm');
      setDeleteConfirm({ show: false, addressId: null, addressIndex: null });
      return;
    }
    
    try {
      // Call delete function from context
      const deleteResult = await handleDeleteAddressById(deleteConfirm.addressId);
      
      // Get fresh list of addresses after deletion
      await refreshAddresses();
      console.log(`[AddressManagementModal] After deletion, remaining addresses: ${addresses.length}`);
      
      // If no addresses left, need to explicitly notify parent to clear selection
      // and mark that we've fetched empty addresses to prevent infinite API calls
      if (addresses.length === 0) {
        console.log('[AddressManagementModal] No addresses left after deletion, clearing selection');
        safeOnAddressSelect({} as AddressWithId); // Pass empty object to clear selection
        setHasEmptyAddressesFetched(true);
      } else {
        // Select the first available address
        console.log('[AddressManagementModal] Selecting first available address after deletion');
        safeOnAddressSelect(addresses[0]);
      }
      
      // Remove success toast to avoid duplicate messages
      // The handleDeleteAddress function in the hook already shows a success toast
    } catch (error) {
      console.error('[AddressManagementModal] Error deleting address:', error);
      
      // Check if the address might already be deleted in database but UI is out of sync
      try {
        // Try to get the address by ID to check if it exists
        const addressExists = await addressService.getAddressById(deleteConfirm.addressId);
        
        if (!addressExists) {
          console.log('[AddressManagementModal] Address already deleted in database, refreshing UI');
          toast.success('Address removed');
          
          // Force refresh addresses to get the current state
          await refreshAddresses();
          
          // Update UI with the refreshed list
          if (addresses.length === 0) {
            safeOnAddressSelect({} as AddressWithId);
            setHasEmptyAddressesFetched(true);
          } else {
            safeOnAddressSelect(addresses[0]);
          }
        } else {
          // Address exists but deletion failed for another reason
          toast.error('Failed to delete address');
        }
      } catch (checkError) {
        console.error('[AddressManagementModal] Error checking address existence:', checkError);
        toast.error('Failed to delete address');
      }
    } finally {
      // Always close confirmation dialog regardless of success or failure
      setDeleteConfirm({ show: false, addressId: null, addressIndex: null });
    }
  };
  
  // Cancel deletion
  const cancelDeleteAddress = () => {
    setDeleteConfirm({ show: false, addressId: null, addressIndex: null });
  };

  // Early return if not open
  if (!isOpen) {
    console.log('[AddressManagementModal] Not rendering because isOpen is false');
    return null;
  }
  
  console.log('[AddressManagementModal] Rendering modal content. Active modal:', activeModal);
  console.log('[AddressManagementModal] onAddressSelect type:', typeof safeOnAddressSelect);
  
  // Return appropriate modal based on active state
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[100] flex items-center justify-center">
      {activeModal === 'address' && (
        <AddressModal
          isOpen={true}
          onClose={handleCloseAll}
          onSelectAddress={safeOnAddressSelect}
          onOpenMapModal={handleOpenMapModal}
          onEditAddress={handleEditAddress}
          // Only provide the selectedAddress as is, don't attempt to fix it automatically in the render
          // This prevents unwanted auto-selection and API calls
          selectedAddress={selectedAddress}
          isSubscription={isSubscription}
          // Pass data to child component
          addresses={addresses}
          isLoading={isLoading}
          error={error}
          // Use handleDeleteAddressRequest instead of handleDeleteAddress
          handleDeleteAddress={handleDeleteAddressRequest}
        />
      )}
      
      {activeModal === 'map' && (
        <GoogleMapsLoader
          onLoad={() => console.log('Google Maps loaded successfully in AddressManagementModal')}
          onError={(error) => {
            console.error('Failed to load Google Maps:', error);
            toast.error('Unable to load map service. Please try again later.');
          }}
        >
          <MapLocationModal 
            isOpen={true}
            onClose={handleCloseMapModal}
            onSelectLocation={handleSelectLocation}
            initialCoords={initialMapCoords}
            // Pass address info in edit mode
            editMode={!!editingAddress}
            addressToEdit={editingAddress}
            userPhone={user?.phone}
            onShowMapModal={handleOpenMapModal}
          />
        </GoogleMapsLoader>
      )}
      
      {/* Delete confirmation dialog */}
      {deleteConfirm.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[110] flex items-center justify-center">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full">
            <h3 className="text-lg font-semibold mb-2">{t('components.addressManagementModal.confirmDelete')}</h3>
            <p className="text-gray-600 mb-6">{t('components.addressManagementModal.deleteWarning')}</p>
            
            <div className="flex justify-end space-x-3 pb-4">
              <button 
                onClick={cancelDeleteAddress}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700"
              >
                {t('common.cancel')}
              </button>
              <button 
                onClick={confirmDeleteAddress}
                className="px-4 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600"
              >
                {t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}